import { useEffect } from 'react';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { Tenant } from '@core/Entities/App/Tenants.entity';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { FilterParams } from '@core/Entities/Search/QueryParams.entity';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import { useAtomValue } from 'jotai';
import isEqual from 'lodash/isEqual';

import stableHash from 'stable-hash';

import {
  Model,
  normalizedMakeAtom,
  normalizedModelAtom,
} from 'Atoms/Search/AggregationsAtom/NormalizedMakesAtom';
import { Logos } from 'Components/SearchWidget/Components/MakeLabel/Logos';
import MakeLabel from 'Components/SearchWidget/Components/MakeLabel/MakeLabel';
import { useFilterLink } from 'Hooks/SearchFilters/UseFilterLink';
import { useSearchFilter } from 'Hooks/SearchFilters/UseSearchFilter';
import { useUpdateFilters } from 'Hooks/SearchFilters/UseUpdateFilters';

import { FilterLink } from '../FilterLink/FilterLink';

const filterId: keyof FilterParams = 'model';

function useModelFilter(
  locale: Locale,
  isPlpFiltersSeoEnabled: boolean,
  tenant: Tenant | undefined,
  applyButton?: boolean,
) {
  const filtersList = useAtomValue(filtersListAtom);
  const activeMakes = (getFilterFromArray('make', filtersList)?.value || []) as string[];
  const activeModels = (getFilterFromArray('model', filtersList)?.value || []) as string[];
  const normalizedModels = useAtomValue(normalizedModelAtom);
  const { updateFilters } = useUpdateFilters();
  const { getLinkForFilterValue } = useFilterLink({
    filterId,
    locale,
    isPlpFiltersSeoEnabled,
    tenant,
  });

  const makeData = useAtomValue(normalizedMakeAtom);

  useEffect(() => {
    if (Object.keys(normalizedModels).length) {
      const updatedModels = activeModels.filter(model =>
        activeMakes.includes(normalizedModels?.[model]?.make),
      );

      if (!isEqual(updatedModels, activeModels))
        updateFilters([{ id: filterId, value: updatedModels }]);
    }
  }, [stableHash(normalizedModels)]);

  const models = activeMakes.reduce((acc, make) => {
    const models = makeData[make] ? makeData[make].modelsPerMake : [];
    return [...acc, ...models];
  }, [] as Model[]);

  const allModels = models.map(model => {
    const maybeLink = getLinkForFilterValue({
      filterValue: model.key,
      resultCount: model.count ?? 0,
    });

    return {
      id: model.key,
      renderLabel: (
        <MakeLabel
          count={model.count}
          make={model.make as Logos}
          name={model.displayName}
          nameRender={<FilterLink link={maybeLink}>{model.displayName}</FilterLink>}
        />
      ),
      label: model.displayName,
      make: makeData[model.make].displayName,
    };
  });

  return useSearchFilter({ filterId: 'model', data: allModels, applyButton });
}

export default useModelFilter;
